import { createForm } from "@tanstack/solid-form";
import { Show } from "solid-js";
import { produce } from "solid-js/store";
import { z } from "zod";
import { useI18n } from "@/api/i18n";
import { CurrencyInput } from "@/components/shared/input";
import { Button } from "@/components/ui/button";
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  FieldContainer,
  FieldContainerLabel,
  FieldInfo,
} from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch, SwitchControl, SwitchThumb } from "@/components/ui/switch";
import { EFFORT, REMOTE_POLICY } from "@/domains/job-offer/create/constants";
import { devLog } from "@/libs/dev-log";
import { parseInputValue } from "@/libs/format";
import {
  createCurrencyValidatorWithMinSchema,
  MIN_AMOUNT_EUR,
} from "@/libs/validators/currency";
import {
  linkedinOnboardingValidationSchema,
  useOnboardingStore,
} from "../../context/OnboardingContext";

const { validationFunction: hourlyRateValidationFunction } =
  createCurrencyValidatorWithMinSchema(MIN_AMOUNT_EUR);

// Extract pricing validation schema from the main schema
const pricingValidationSchema = linkedinOnboardingValidationSchema.pick({
  pricing: true,
});

// Extended schema for availability fields
const extendedPricingSchema = pricingValidationSchema.shape.pricing.extend({
  is_available: z.boolean().default(true),
  work_preference: z.enum(REMOTE_POLICY).default(REMOTE_POLICY[0]),
  weekly_hours: z.enum(EFFORT),
});

type PricingFormValues = z.infer<typeof extendedPricingSchema>;

interface PricingModalProps {
  onClose: () => void;
}

export const PricingModal = (props: PricingModalProps) => {
  const [store, setStore] = useOnboardingStore();
  const { t } = useI18n();

  const form = createForm(() => ({
    defaultValues: {
      price_per_day: store.pricing.value.price_per_day
        ? Number(store.pricing.value.price_per_day)
        : undefined,
      is_available: store.info.value.is_available ?? true,
      weekly_hours:
        store.info.value.weekly_hours ?? ("40" as (typeof EFFORT)[number]),
      work_preference: store.info.value.work_preference ?? REMOTE_POLICY[0],
    } as PricingFormValues,
    onSubmit: async ({ value }) => {
      const validatedData = extendedPricingSchema.safeParse(value);
      if (!validatedData.success) {
        devLog(validatedData.error);
        return;
      }

      devLog(validatedData.data);

      // Update store with validated data
      setStore(
        produce((draft) => {
          draft.pricing.value.price_per_day = parseInputValue(
            validatedData.data.price_per_day.toString(),
          );
          draft.pricing.error = null;
          draft.info.value.is_available = validatedData.data.is_available;
          draft.info.value.weekly_hours = validatedData.data.weekly_hours;
          draft.info.value.work_preference = validatedData.data.work_preference;
        }),
      );

      props.onClose();
    },
  }));

  return (
    <div class="flex flex-col gap-6">
      <DialogHeader>
        <DialogTitle>{t("onboarding.steps.pricing.title")}</DialogTitle>
        <DialogDescription>
          {t("onboarding.steps.pricing.description")}
        </DialogDescription>
      </DialogHeader>

      <div class="flex flex-col gap-6">
        {/* Disponibilità Section */}
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-1">
            <h2 class="text-lg font-medium">{t("availability.title")}</h2>
            <p class="text-sm text-gray-500">
              Indica se sei disponibile per lavorare su nuovi progetti
            </p>
          </div>
          <div class="bg-gray-50 p-4 rounded-lg flex flex-col gap-4 border border-gray-200">
            <form.Field
              name="is_available"
              validators={{
                onChange: z.boolean(),
              }}
            >
              {(field) => (
                <div class="flex items-center gap-3">
                  <Switch
                    checked={field().state.value}
                    onChange={(v) => {
                      field().setValue(v);
                    }}
                  >
                    <SwitchControl>
                      <SwitchThumb />
                    </SwitchControl>
                  </Switch>
                  <span class="text-sm font-medium">
                    Disponibile per nuovi progetti
                  </span>
                </div>
              )}
            </form.Field>
            <form.Subscribe
              selector={(state) => ({
                is_available: state.values.is_available,
              })}
            >
              {(state) => (
                <div class="flex flex-col md:flex-row items-start gap-4">
                  <form.Field
                    name="weekly_hours"
                    validators={{
                      onChange: extendedPricingSchema.shape.weekly_hours,
                    }}
                  >
                    {(field) => (
                      <FieldContainer class="flex-1">
                        <FieldContainerLabel for={field().name}>
                          {t("availability.weekly_hours")}
                        </FieldContainerLabel>
                        <Select
                          disabled={!state().is_available}
                          class="bg-white"
                          value={field().state.value}
                          onChange={(value) => {
                            const nextValue = value ?? field().state.value;
                            field().handleChange(nextValue);
                          }}
                          options={[...EFFORT]}
                          itemComponent={(item) => (
                            <SelectItem {...item}>
                              {item.item.rawValue}{" "}
                              {t("availability.h_weekly_hours")}
                            </SelectItem>
                          )}
                        >
                          <SelectTrigger>
                            <SelectValue>
                              <Show
                                when={field().state.value}
                                fallback={t("availability.select_hours")}
                              >
                                {field().state.value}{" "}
                                {t("availability.h_weekly_hours")}
                              </Show>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent />
                        </Select>
                        <FieldInfo field={field()} />
                      </FieldContainer>
                    )}
                  </form.Field>
                  <form.Field
                    name="work_preference"
                    validators={{
                      onChange: z.enum(REMOTE_POLICY),
                    }}
                  >
                    {(field) => (
                      <FieldContainer class="flex-1">
                        <FieldContainerLabel for={field().name}>
                          {t("profile.edit.dialog.workPreference")}
                        </FieldContainerLabel>
                        <Select
                          class="bg-white"
                          value={field().state.value}
                          onChange={(value) =>
                            field().handleChange(value ?? field().state.value)
                          }
                          options={[...REMOTE_POLICY]}
                          itemComponent={(item) => (
                            <SelectItem {...item}>
                              {t(
                                `common.remote_policy_options.${item.item.rawValue as (typeof REMOTE_POLICY)[number]}`,
                              )}
                            </SelectItem>
                          )}
                        >
                          <SelectTrigger>
                            <SelectValue>
                              <Show
                                when={field().state.value}
                                fallback={t(
                                  "profile.edit.dialog.workPreferencePlaceholder",
                                )}
                              >
                                {t(
                                  `common.remote_policy_options.${field().state.value as (typeof REMOTE_POLICY)[number]}`,
                                )}
                              </Show>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent />
                        </Select>
                        <FieldInfo field={field()} />
                      </FieldContainer>
                    )}
                  </form.Field>
                </div>
              )}
            </form.Subscribe>
          </div>
        </div>

        {/* Tariffa oraria Section */}
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-1">
            <h2 class="text-lg font-medium">
              {t("onboarding.steps.pricing.dailyRate")}
            </h2>
            <p class="text-sm text-gray-500">
              Compenso richiesto per ogni giornata di lavoro
            </p>
          </div>
          <form.Field
            name="price_per_day"
            validators={{
              onBlur: ({ value }) => {
                const result = hourlyRateValidationFunction(value);
                if (!result.isValid) {
                  return result.error;
                }
              },
            }}
          >
            {(field) => (
              <FieldContainer>
                <div class="relative">
                  <CurrencyInput
                    class="w-full pr-12"
                    value={field().state.value ?? ""}
                    fieldApi={field()}
                    placeholder="Inserisci tariffa giornaliera"
                  />
                  <span class="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">
                    /giorno
                  </span>
                </div>
                <FieldInfo
                  field={field()}
                  helpText="Consigliato: €250-€400/giorno a seconda della tua esperienza"
                />
              </FieldContainer>
            )}
          </form.Field>
        </div>
      </div>

      <div class="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={props.onClose}>
          {t("common.cancel")}
        </Button>
        <form.Subscribe
          selector={(state) => ({
            canSubmit: state.canSubmit,
            isSubmitting: state.isSubmitting,
            isValid: state.isValid,
            isSubmittable: extendedPricingSchema.safeParse(state.values)
              .success,
          })}
        >
          {(state) => (
            <Button
              onClick={form.handleSubmit}
              disabled={
                !state().isValid ||
                state().isSubmitting ||
                !state().isSubmittable
              }
            >
              {state().isSubmitting ? t("common.saving") : t("common.save")}
            </Button>
          )}
        </form.Subscribe>
      </div>
    </div>
  );
};
